/**
 * `asOfAdapter` — adapt the as-of-date projection's node shape to the shape
 * the existing tree pipeline (`buildTree` → `layoutTree` → `<TaskCanvas/>`)
 * already consumes. Pure + dependency-free.
 *
 * Issue #764 (Phase 3, B3.3 rewind — the second hero). The rewind read
 * endpoint (`GET …/tasks/tree/as-of?at=…`) returns `NodeStateAsOf[]`, a
 * *replay* shape derived from the event log. It differs from the live
 * `TaskNodeWithAssignments` in two small ways:
 *
 *   1. `assignments` is a bare `membership_id[]` (the replay only tracks who
 *      was assigned, not the join-row metadata), where the live tree carries
 *      `{ membership_id }[]`.
 *   2. it carries an `archived` boolean — a node archived BEFORE `at` is
 *      included with `archived=true` (so the past tree shows the node as it
 *      stood), where the live "now" tree simply omits archived nodes.
 *
 * Rather than fork `buildTree`, we adapt the as-of nodes into the one
 * `FlatTaskNode` shape `buildTree` already understands. This keeps the whole
 * rewind surface rendering through the SAME canvas pipeline the live editor
 * uses (the clean reuse the founder's bar asks for — §A7) — the rewind is
 * just "the same tree, fed a past node set, read-only."
 *
 * Archived handling: by default we DROP archived nodes (a node archived
 * before `at` is no longer part of the plan as it stood — it had been
 * removed). The optional `includeArchived` flag keeps them, mirroring the
 * live `include_archived` choice, should a future surface want to show them
 * dimmed. We never *invent* state; we only translate the wire shape.
 *
 * Brand firewall (§A9): this is a translation of the PLAN's node set. The
 * `membership_id`s become assignee avatars (collaborative sight), never an
 * aggregate about a person.
 *
 * Isolation: imports only the generated schema types + the tree's own
 * `FlatTaskNode` — no scheduler coupling, no network.
 */

import type { components } from "../../api/schema.gen";
import type { FlatTaskNode } from "../tree/buildTree";

/** The per-node payload of the as-of projection (B3.3), from the wire. */
export type NodeStateAsOf = components["schemas"]["NodeStateAsOf"];

export interface AdaptAsOfOptions {
  /**
   * Keep nodes that were archived at or before `at` (rendered as part of the
   * past tree). Defaults `false` — archived-then is treated as removed-then,
   * so the rewound tree shows the plan minus what had already been cut.
   */
  includeArchived?: boolean;
}

/**
 * Adapt one as-of node into the `FlatTaskNode` shape `buildTree` consumes.
 * The replay shape has no estimate / target-date (those aren't part of the
 * projection), so they read as `null` — display-only fields the past tree
 * simply doesn't surface.
 */
function toFlatNode(node: NodeStateAsOf): FlatTaskNode {
  return {
    id: node.id,
    parent_id: node.parent_id,
    title: node.title,
    description: node.description,
    state: node.state,
    order_index: node.order_index,
    estimate_minutes: null,
    target_date: null,
    assignments: node.assignments.map((membership_id) => ({ membership_id })),
  };
}

/**
 * Adapt an as-of node set into the flat list `buildTree` understands.
 * Archived-then nodes are dropped unless `includeArchived` is set.
 */
export function adaptAsOfNodes(
  nodes: readonly NodeStateAsOf[],
  options: AdaptAsOfOptions = {},
): FlatTaskNode[] {
  const { includeArchived = false } = options;
  return nodes.filter((n) => includeArchived || !n.archived).map(toFlatNode);
}
